import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Container, Form } from 'react-bootstrap';
import SingleHistoryElement from './SingleHistoryElement';
import { getFieldConfigurations, getTestCaseData } from '../../../../modules/config';
import { getTestCaseHistory } from '../../../../modules/entity-details';
import SkeletonLoader from '../../../elements/SkeletonLoader';

const TestCaseHistory = ({ entityId, users, projectId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [modules, setModules] = useState([]);
  const [labels, setLabels] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [sort, setSort] = useState('Newest First');
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getTestCaseHistory(entityId);
      setData(response.data);
      setError(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  const fetchOtherData = async () => {
    setLoading(true);
    try {
      const response = await getTestCaseData(projectId);
      if (response) {
        const fetchedStatuses = response?.result?.filter(
          (item) => item.group === 'TEST_CASE_STATUS'
        );
        const fetchedPriorities = response?.result?.filter(
          (item) => item.group === 'TEST_CASE_PRIORITY'
        );
        const fetchedLabels = response?.result?.filter(
          (item) => item.group === 'LABEL'
        );
        const fetchedModules = response?.result?.filter(
          (item) => item.group === 'FUNCTIONAL_MODULE_FIELD'
        );
        const testSuitesResponse = response?.test_suites;
        setTestSuites(testSuitesResponse);
        setStatuses(fetchedStatuses);
        setPriorities(fetchedPriorities);
        setLabels(fetchedLabels);
        setModules(fetchedModules);
      }
      const fieldResponse = await getFieldConfigurations(projectId);

      const categories = (fieldResponse?.data || []).map(cat => ({
        ...cat,
        subCategories: cat.sub_categories || [],
      }));

      setFieldConfigs(categories);
    } catch (error) {
      console.error('Error fetching test case data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchOtherData();
    return () => {
      setData([]);
    };
  }, [entityId]);

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) =>
      sort === 'Newest First'
        ? new Date(b.created_at) - new Date(a.created_at)
        : new Date(a.created_at) - new Date(b.created_at)
    );
  }, [data, sort]);

  const groupByDateTime = useCallback(() => {
    return sortedData.reduce((acc, item) => {
      const dateTimeKey = `${new Date(item.created_at)
        .toISOString()
        .slice(0, 16)}-${item.event_type}`;

      if (!acc[dateTimeKey]) {
        acc[dateTimeKey] = [];
      }

      acc[dateTimeKey].push(item);
      return acc;
    }, {});
  }, [sortedData]);

  const groupedData = Object.values(groupByDateTime());
  return (
    <Card className="shadow-sm">
      <Card.Header
        className="d-flex justify-content-between align-items-center"
        style={{ height: '50px' }}>
        <h5 className="mb-0">History</h5>
        {groupedData.length > 0 && (
          <Form.Select
            style={{ width: '150px' }}
            value={sort}
            onChange={(e) => setSort(e.target.value)}>
            <option>Newest First</option>
            <option>Oldest First</option>
          </Form.Select>
        )}
      </Card.Header>
      <Card.Body>
        <Container fluid>
          {loading && <SkeletonLoader />}
          {error && <p>{error}</p>}
          {groupedData &&
            groupedData.map((item, index) => (
              <SingleHistoryElement
                key={index}
                item={item}
                users={users}
                statuses={statuses}
                priorities={priorities}
                modules={modules}
                labels={labels}
                testSuites={testSuites}
                fieldConfigs={fieldConfigs}
              />
            ))}
        </Container>
      </Card.Body>
    </Card>
  );
};

export default TestCaseHistory;
